import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bell, PartyPopper, X } from 'lucide-react';
import type { BucketId, CompletionStage } from '../../lib/steps';

interface ProgressBannerProps {
  applicationId: string | number;
  pct: number;
  done: number;
  total: number;
  /** Two-stage completion signal (useCompletion): swaps the in-progress banner
   *  for a celebratory one once the applicant has done their part / finished. */
  stage: CompletionStage;
  /** Deep-link target. `bucketId` is the 3-phase contract's section id;
   *  `phaseKey` is kept alongside it so a banner rendered by an older bundle (or
   *  a link already in flight) still resolves — see resolvePhaseSection. */
  nextStep?: { title: string; stepKey: string; phaseKey: string; bucketId?: BucketId };
}

const dismissKey = (id: string | number, kind: string) => `pb-dismissed:${kind}:${id}`;

/**
 * Shell banner (PRD §6.3). Three states, keyed off completion stage:
 *   - in progress → next step + Continue link over a progress bar (dismissible
 *     per application, returns next session; auto-hides at 100% as a safety).
 *   - awaiting_references → "You've done your part!" — the applicant finished
 *     everything they can and submitted their referees; we're collecting refs.
 *   - complete → "Your application is complete!"
 * Each state has its own dismiss key so dismissing the progress banner early
 * never suppresses the later celebration. Reading sessionStorage per render
 * (not once at mount) keeps dismissal correct as the stage transitions on a
 * mounted shell.
 */
export default function ProgressBanner({ applicationId, pct, done, total, stage, nextStep }: ProgressBannerProps) {
  const kind = stage === 'complete' ? 'complete' : stage === 'awaiting_references' ? 'awaiting' : 'progress';
  const [, force] = useState(0);

  const dismissed = sessionStorage.getItem(dismissKey(applicationId, kind)) === '1';
  const dismiss = () => {
    sessionStorage.setItem(dismissKey(applicationId, kind), '1');
    force((n) => n + 1);
  };

  if (dismissed) return null;
  // Safety: never show the in-progress banner once every step is done.
  if (kind === 'progress' && pct >= 100) return null;

  if (kind !== 'progress') {
    const complete = kind === 'complete';
    return (
      <div className="completebanner" role="status" aria-label="Application completion">
        <button className="iconbtn dismiss" aria-label="Dismiss message" onClick={dismiss}>
          <X size={16} aria-hidden />
        </button>
        <span className="cb-icon" aria-hidden>
          <PartyPopper size={22} />
        </span>
        <div className="cb-text">
          <strong>{complete ? '🎉 Your application is complete!' : "🎉 You've done your part!"}</strong>
          <span>
            {complete
              ? 'Every step is done and submitted — nice work! There’s nothing more you need to do right now.'
              : `You’ve finished everything you can for now (${done} of ${total} steps complete). We’re just waiting on your references to come back — you don’t need to do anything else.`}
          </span>
        </div>
      </div>
    );
  }

  const stepTitle = nextStep?.title ?? 'your application';

  return (
    <div className="progressbanner" role="region" aria-label="Application progress">
      <button className="iconbtn dismiss" aria-label="Dismiss progress banner" onClick={dismiss}>
        <X size={16} aria-hidden />
      </button>

      <div className="pb-row pb-top">
        <span className="pb-next">
          <Bell size={16} aria-hidden />
          Next step: <strong>{stepTitle}</strong> ({done} of {total} steps complete)
        </span>
        {nextStep && (
          <Link
            to="/application"
            state={{ stepKey: nextStep.stepKey, phaseKey: nextStep.phaseKey, bucketId: nextStep.bucketId }}
            className="continuelink"
          >
            Continue: {stepTitle} <ArrowRight size={15} aria-hidden />
          </Link>
        )}
      </div>

      <div className="pb-row pb-meta">
        <span>Progress: {done} of {total} completed</span>
        <span className="pct">{pct}%</span>
      </div>

      <div className="progressbar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
